import { z } from 'zod';
import { auditFieldsSchema, centsSchema, idSchema, isoDateSchema, paginated, paginationQuerySchema, positiveCentsSchema, } from './common.js';
import { documentSchema } from './documents.js';
import { paymentMethodSchema } from './financeiro.js';
/**
 * Clube AMAI Pass.
 *
 * O plano é vendido por LOTE: cada lote tem preço e quota próprios, e o preço
 * sobe conforme os lotes esgotam — a mesma mecânica de pré-venda que financia
 * a obra hoje. O associado é um VÍNCULO sobre a Pessoa (um CPF, um cadastro);
 * a venda gera a conta a receber e o contrato sai de um template por plano.
 */
/* ================================= Planos ================================ */
export const MAINTENANCE_FREQUENCIES = ['MENSAL', 'SEMESTRAL', 'ANUAL', 'VITALICIA'];
export const maintenanceFrequencySchema = z.enum(MAINTENANCE_FREQUENCIES);
export const MAINTENANCE_FREQUENCY_LABELS = {
    MENSAL: 'Mensal',
    SEMESTRAL: 'Semestral',
    ANUAL: 'Anual',
    VITALICIA: 'Vitalícia (sem manutenção)',
};
export const memberPlanSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    name: z.string(),
    lotNumber: z.number().int(),
    maxPeople: z.number().int(),
    quota: z.number().int(),
    /// Associados não rescindidos/transferidos deste plano — vagas consumidas.
    soldCount: z.number().int(),
    acquisitionCents: z.number().int(),
    dependentCents: z.number().int(),
    maintenanceCents: z.number().int(),
    maintenanceFrequency: maintenanceFrequencySchema,
    costCenterId: idSchema,
    costCenterName: z.string().nullable(),
    chartAccountId: idSchema,
    chartAccountName: z.string().nullable(),
    benefits: z.string().nullable(),
    contractTemplate: z.string().nullable(),
    internalNotes: z.string().nullable(),
    isActive: z.boolean(),
});
export const memberPlanInputSchema = z.object({
    name: z.string().trim().min(2, 'Informe o nome do plano').max(120),
    lotNumber: z.number().int().min(1).default(1),
    maxPeople: z.number().int().min(1, 'O plano cobre ao menos 1 pessoa').max(50).default(1),
    quota: z.number().int().min(0, 'Quota não pode ser negativa').default(0),
    acquisitionCents: positiveCentsSchema,
    dependentCents: centsSchema.min(0).default(0),
    maintenanceCents: centsSchema.min(0).default(0),
    maintenanceFrequency: maintenanceFrequencySchema.default('ANUAL'),
    costCenterId: idSchema,
    chartAccountId: idSchema,
    benefits: z.string().trim().max(4000).optional().nullable(),
    contractTemplate: z.string().trim().max(20000).optional().nullable(),
    internalNotes: z.string().trim().max(2000).optional().nullable(),
    isActive: z.boolean().default(true),
});
/* =============================== Associados ============================== */
export const MEMBER_STATUSES = [
    'CONTRATO_PENDENTE',
    'ATIVO',
    'INADIMPLENTE',
    'INATIVO',
    'TRANSFERIDO',
    'RESCINDIDO',
];
export const memberStatusSchema = z.enum(MEMBER_STATUSES);
export const MEMBER_STATUS_LABELS = {
    CONTRATO_PENDENTE: 'Contrato pendente',
    ATIVO: 'Ativo',
    INADIMPLENTE: 'Inadimplente',
    INATIVO: 'Inativo',
    TRANSFERIDO: 'Transferido',
    RESCINDIDO: 'Rescindido',
};
export const DEPENDENT_TYPES = ['ADULTO', 'INFANTIL', 'SENIOR'];
export const dependentTypeSchema = z.enum(DEPENDENT_TYPES);
export const DEPENDENT_TYPE_LABELS = {
    ADULTO: 'Adulto',
    INFANTIL: 'Infantil',
    SENIOR: 'Sênior',
};
export const memberDependentSchema = z.object({
    id: idSchema,
    name: z.string(),
    document: z.string().nullable(),
    birthDate: z.string().nullable(),
    kinship: z.string().nullable(),
    type: dependentTypeSchema,
});
export const memberSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    personId: idSchema,
    name: z.string(),
    document: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    registration: z.string(),
    planId: idSchema,
    planName: z.string().nullable(),
    status: memberStatusSchema,
    planStartDate: z.string(),
    renewalDate: z.string().nullable(),
    paymentDay: z.number().int().nullable(),
    /// Competência da primeira manutenção. Nulo = fora da geração automática.
    feesStartDate: z.string().nullable(),
    acquisitionCents: z.number().int(),
    maintenanceCents: z.number().int(),
    maintenanceFrequency: maintenanceFrequencySchema,
    contractText: z.string().nullable(),
    contractAcceptedAt: z.string().nullable(),
    qrToken: z.string(),
    sellerId: idSchema.nullable(),
    sellerName: z.string().nullable(),
    financialEntryId: idSchema.nullable(),
    /// Código do lançamento gerado na venda, para o operador achar no Financeiro.
    financialEntryCode: z.string().nullable(),
    /// Quanto da venda ainda está em aberto no contas a receber.
    openCents: z.number().int(),
    statusReason: z.string().nullable(),
    notes: z.string().nullable(),
    dependents: z.array(memberDependentSchema),
});
const dependentInputSchema = z.object({
    name: z.string().trim().min(2, 'Informe o nome do dependente').max(180),
    document: documentSchema.optional().nullable(),
    birthDate: isoDateSchema.optional().nullable(),
    kinship: z.string().trim().max(60).optional().nullable(),
    type: dependentTypeSchema.default('ADULTO'),
});
/** Linha de recebimento da venda presencial — pagamento misto é a regra. */
const salePaymentSchema = z.object({
    method: paymentMethodSchema,
    amountCents: positiveCentsSchema,
    bankAccountId: idSchema,
});
/**
 * Venda presencial.
 *
 * O titular pode ser uma pessoa já cadastrada (personId) ou vir com os dados
 * mínimos para o cadastro nascer aqui — sem sair da tela de venda.
 */
export const memberSaleInputSchema = z
    .object({
    planId: idSchema,
    personId: idSchema.optional().nullable(),
    person: z
        .object({
        name: z.string().trim().min(2, 'Informe o nome do titular').max(180),
        document: documentSchema,
        birthDate: isoDateSchema.optional().nullable(),
        phone: z.string().trim().max(20).optional().nullable(),
        whatsapp: z.string().trim().max(20).optional().nullable(),
        email: z.string().trim().max(180).optional().nullable(),
        city: z.string().trim().max(120).optional().nullable(),
        uf: z.string().trim().length(2).toUpperCase().optional().nullable(),
    })
        .optional()
        .nullable(),
    planStartDate: isoDateSchema,
    paymentDay: z.number().int().min(1).max(28).optional().nullable(),
    dependents: z.array(dependentInputSchema).max(49).default([]),
    payments: z.array(salePaymentSchema).max(10).default([]),
    notes: z.string().trim().max(2000).optional().nullable(),
})
    .refine((v) => v.personId || v.person, {
    message: 'Informe o titular: uma pessoa cadastrada ou os dados do cadastro',
    path: ['personId'],
});
export const memberUpdateSchema = z.object({
    paymentDay: z.number().int().min(1).max(28).optional().nullable(),
    /// Ligar/desligar a manutenção automática deste associado, e a partir de
    /// qual competência. Nulo desliga — o que já foi gerado continua de pé.
    feesStartDate: isoDateSchema.optional().nullable(),
    renewalDate: isoDateSchema.optional().nullable(),
    notes: z.string().trim().max(2000).optional().nullable(),
    dependents: z.array(dependentInputSchema).max(49).optional(),
});
/** Transições manuais de status; a justificativa vai para a auditoria. */
export const memberStatusChangeSchema = z.object({
    status: memberStatusSchema,
    reason: z.string().trim().max(500).optional().nullable(),
});
export const acceptContractSchema = z.object({
    /// Registro de COMO o aceite aconteceu (assinou impresso, aceitou no balcão).
    note: z.string().trim().max(300).optional().nullable(),
});
export const memberListQuerySchema = paginationQuerySchema.extend({
    status: memberStatusSchema.optional(),
    planId: idSchema.optional(),
});
export const memberListResponseSchema = paginated(memberSchema);
/* ================================ Restritos ============================== */
export const restrictedPersonSchema = z.object({
    id: idSchema,
    companyId: idSchema,
    document: z.string(),
    name: z.string(),
    reason: z.string().nullable(),
    createdAt: z.string(),
    createdById: idSchema.nullable(),
});
export const restrictedPersonInputSchema = z.object({
    document: documentSchema,
    name: z.string().trim().min(2, 'Informe o nome').max(180),
    reason: z.string().trim().min(5, 'Explique o motivo do bloqueio').max(500),
});
export const restrictedListQuerySchema = paginationQuerySchema;
export const restrictedListResponseSchema = paginated(restrictedPersonSchema);
/* ============================ Painel de vendas =========================== */
export const salesPanelQuerySchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    sellerId: idSchema.optional(),
});
export const salesPanelRowSchema = z.object({
    memberId: idSchema,
    soldAt: z.string(),
    sellerName: z.string().nullable(),
    memberName: z.string(),
    planName: z.string(),
    status: memberStatusSchema,
    totalCents: z.number().int(),
    receivedCents: z.number().int(),
});
export const salesPanelResponseSchema = z.object({
    rows: z.array(salesPanelRowSchema),
    totals: z.object({
        count: z.number().int(),
        totalCents: z.number().int(),
        receivedCents: z.number().int(),
    }),
});
/* ============================== Mensalidades ============================= */
/**
 * Manutenção periódica do associado.
 *
 * O título é vendido uma vez (adesão); a manutenção volta todo período —
 * mensal, semestral ou anual, conforme o plano. VITALICIA não gera nada: é o
 * plano em que a manutenção já está paga para sempre.
 *
 * A competência é sempre o PRIMEIRO DIA DO MÊS. É ela que identifica a
 * manutenção daquele associado naquele período, no banco e na tela — o dia do
 * vencimento pode mudar (o associado pede outro dia), a competência não.
 */
export const membershipCompetenceSchema = z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Use o formato AAAA-MM');
export const membershipFeeLineSchema = z.object({
    memberId: idSchema,
    registration: z.string(),
    memberName: z.string(),
    planName: z.string(),
    /// AAAA-MM.
    competence: z.string(),
    dueDate: z.string(),
    amountCents: z.number().int(),
    frequency: maintenanceFrequencySchema,
    /// Preenchidos só na geração — na prévia ainda não existe lançamento.
    entryId: idSchema.nullable().default(null),
    entryCode: z.string().nullable().default(null),
    /// Cobrança emitida no gateway junto com a manutenção, quando a emissão
    /// automática está ligada.
    chargeId: idSchema.nullable().default(null),
    /// Por que a cobrança NÃO saiu, em português. A conta a receber existe do
    /// mesmo jeito — o que faltou foi mandar o cliente pagar.
    chargeIssue: z.string().nullable().default(null),
});
/**
 * Manutenção que NÃO foi gerada, e por quê — em português, dizendo o que fazer.
 * A prévia mostra isso junto com o que vai ser gerado: o operador precisa ver
 * quem ficou de fora antes de confirmar, não descobrir depois pela ausência.
 */
export const membershipFeeBlockedSchema = z.object({
    memberId: idSchema,
    registration: z.string(),
    memberName: z.string(),
    competence: z.string(),
    reason: z.string(),
});
export const membershipFeeRunQuerySchema = z.object({
    /// Até que competência considerar. Vazio = o mês corrente.
    competenceTo: membershipCompetenceSchema.optional(),
    memberId: idSchema.optional(),
});
export const membershipFeeRunInputSchema = membershipFeeRunQuerySchema;
export const membershipFeeRunSchema = z.object({
    competenceTo: z.string(),
    lines: z.array(membershipFeeLineSchema),
    blocked: z.array(membershipFeeBlockedSchema),
    totalCents: z.number().int(),
});
/* ========================= Régua de inadimplência ======================== */
/**
 * A régua é do OPERADOR.
 *
 * Dias e ações são configuráveis porque cobrança é política comercial: o dia
 * em que se avisa, o dia em que se marca inadimplente e o dia em que se fecha
 * a catraca mudam com a temporada e com a decisão do Diretor. Fixar isso no
 * código seria pedir deploy toda vez que ele mudasse de ideia.
 */
export const DUNNING_ACTIONS = ['AVISAR', 'MARCAR_INADIMPLENTE', 'BLOQUEAR_ACESSO'];
export const dunningActionSchema = z.enum(DUNNING_ACTIONS);
export const DUNNING_ACTION_LABELS = {
    AVISAR: 'Avisar o associado',
    MARCAR_INADIMPLENTE: 'Marcar como inadimplente',
    BLOQUEAR_ACESSO: 'Bloquear a entrada',
};
export const dunningStepSchema = auditFieldsSchema.extend({
    id: idSchema,
    companyId: idSchema,
    /// Dias a partir do vencimento: negativo avisa antes, 0 no dia.
    offsetDays: z.number().int(),
    action: dunningActionSchema,
    templateName: z.string().nullable(),
    templateLanguage: z.string(),
    label: z.string(),
    isActive: z.boolean(),
    /// Quantas vezes este degrau já rodou — o operador vê se a régua pega.
    timesRun: z.number().int(),
});
export const dunningStepInputSchema = z.object({
    offsetDays: z.number().int().min(-60, 'No máximo 60 dias antes').max(365, 'No máximo um ano depois'),
    action: dunningActionSchema,
    templateName: z.string().trim().max(120).optional().nullable(),
    templateLanguage: z.string().trim().max(10).default('pt_BR'),
    label: z.string().trim().min(3, 'Dê um nome ao degrau').max(80),
    isActive: z.boolean().default(true),
});
/** Uma linha do que a régua fez — ou do que ela faria, na prévia. */
export const dunningLineSchema = z.object({
    stepId: idSchema,
    stepLabel: z.string(),
    action: dunningActionSchema,
    memberId: idSchema,
    registration: z.string(),
    memberName: z.string(),
    installmentId: idSchema,
    entryCode: z.string(),
    dueDate: z.string(),
    openCents: z.number().int(),
    daysOverdue: z.number().int(),
    /// Preenchidos na execução: o que aconteceu de fato.
    done: z.boolean().default(false),
    detail: z.string().nullable().default(null),
});
export const dunningRunSchema = z.object({
    lines: z.array(dunningLineSchema),
    /// Associados que saíram do bloqueio porque a parcela foi paga.
    released: z.array(z.object({ memberId: idSchema, registration: z.string(), memberName: z.string() })),
});
/** Quem está devendo, para a tela de inadimplência. */
export const overdueMemberSchema = z.object({
    memberId: idSchema,
    registration: z.string(),
    memberName: z.string(),
    document: z.string().nullable(),
    phone: z.string().nullable(),
    status: memberStatusSchema,
    accessBlockedAt: z.string().nullable(),
    accessBlockReason: z.string().nullable(),
    /// Quantas parcelas vencidas e quanto somam.
    overdueCount: z.number().int(),
    overdueCents: z.number().int(),
    /// Dias de atraso da parcela MAIS ANTIGA — é o número que decide o degrau.
    worstDaysOverdue: z.number().int(),
    oldestDueDate: z.string(),
});
export const overdueListResponseSchema = z.object({
    data: z.array(overdueMemberSchema),
    totals: z.object({ members: z.number().int(), cents: z.number().int() }),
});
//# sourceMappingURL=clube.js.map